import { sectionRenderer } from '@theme/section-renderer';

/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

const CART_DRAWER_SELECTOR = 'theme-drawer#cart-drawer';
const CART_DRAWER_SECTION_ID = 'cart-drawer-section';

/** @type {Promise<void> | null} */
let pendingCartDrawerOpen = null;

/** @type {number | undefined} */
let pendingCartDrawerReset;

function getCartDrawer() {
  return /** @type {HTMLElement & {open?: () => void} | null} */ (document.querySelector(CART_DRAWER_SELECTOR));
}

/**
 * Keeps stale or empty cart content covered while an add request is in flight.
 * @param {HTMLElement} drawer
 * @param {boolean} pending
 */
function setCartDrawerPending(drawer, pending) {
  drawer.toggleAttribute('data-cart-update-pending', pending);
  drawer.querySelector('dialog')?.toggleAttribute('aria-busy', pending);
  drawer.querySelector('.cart-items-component')?.toggleAttribute('inert', pending);

  if (pendingCartDrawerReset) window.clearTimeout(pendingCartDrawerReset);
  pendingCartDrawerReset = undefined;

  // Third-party cart apps do not always emit an error/completion event. Never
  // leave the drawer blocked indefinitely if one of those requests fails.
  if (pending) {
    pendingCartDrawerReset = window.setTimeout(() => setCartDrawerPending(drawer, false), 15000);
  }
}

/**
 * Opens immediately for normal product forms, but waits for an originating
 * modal (quick add) to close before entering the top layer.
 * @param {HTMLElement & {open?: () => void}} drawer
 * @param {HTMLDialogElement | null} [sourceModal]
 */
function openCartDrawer(drawer, sourceModal = null) {
  const open = () => {
    if (drawer.hasAttribute('open')) return;
    if (drawer.open) drawer.open();
    else {
      customElements.whenDefined('theme-drawer').then(() => {
        if (!drawer.hasAttribute('open')) drawer.open?.();
      });
    }
  };

  if (sourceModal?.open) sourceModal.addEventListener('close', open, { once: true });
  else open();
}

/**
 * Gives add-to-cart submissions immediate visual feedback, including flows
 * intercepted by SlideCart before ProductFormComponent receives the submit.
 * @param {HTMLFormElement} form
 */
function prepareCartDrawerForForm(form) {
  if (!form.closest('product-form-component') || !form.checkValidity()) return;
  const action = new URL(form.action, window.location.href);
  if (!action.pathname.endsWith('/cart/add')) return;

  const drawer = getCartDrawer();
  if (!drawer || drawer.hasAttribute('open') || !drawer.querySelector('cart-drawer-component[auto-open]')) return;

  const sourceModal = /** @type {HTMLDialogElement | null} */ (form.closest('dialog:modal'));
  setCartDrawerPending(drawer, true);
  openCartDrawer(drawer, sourceModal);
}

/** @param {SubmitEvent} event */
function prepareCartDrawerForSubmit(event) {
  if (event.target instanceof HTMLFormElement) prepareCartDrawerForForm(event.target);
}

/**
 * Some cart apps intercept the submitter's click and only publish a cart event
 * after their network request. Capture the intent before those handlers run.
 * @param {MouseEvent} event
 */
function prepareCartDrawerForAddClick(event) {
  if (!(event.target instanceof Element)) return;

  const submitter = event.target.closest('button[type="submit"][name="add"]');
  if (!(submitter instanceof HTMLButtonElement) || submitter.disabled) return;

  const form = submitter.form;
  if (form) prepareCartDrawerForForm(form);
}

function syncCartCount(drawer) {
  const countText = drawer.querySelector('[ref="cartItemCount"]')?.textContent;
  const itemCount = countText ? Number.parseInt(countText, 10) : 0;
  if (!Number.isFinite(itemCount)) return;

  for (const cartIcon of document.querySelectorAll('cart-icon')) {
    if ('renderCartBubble' in cartIcon && typeof cartIcon.renderCartBubble === 'function') {
      void cartIcon.renderCartBubble(itemCount);
    }
  }

  const liveRegion = document.querySelector('header-actions [ref="liveRegion"]');
  if (liveRegion) liveRegion.textContent = `${Theme.translations.cart_count}: ${itemCount}`;
}

async function refreshAndOpenCartDrawer() {
  if (pendingCartDrawerOpen) return pendingCartDrawerOpen;

  pendingCartDrawerOpen = (async () => {
    const drawer = getCartDrawer();

    if (!drawer) {
      window.location.href = Theme.routes.cart_url || '/cart';
      return;
    }

    setCartDrawerPending(drawer, true);
    openCartDrawer(drawer);

    try {
      await sectionRenderer.renderSection(CART_DRAWER_SECTION_ID, {
        cache: false,
        mode: 'hydration',
        injectStylesheet: true,
      });
    } catch (error) {
      console.warn('[cart-drawer] Could not refresh before opening:', error);
    } finally {
      setCartDrawerPending(drawer, false);
    }

    syncCartCount(drawer);
  })().finally(() => {
    pendingCartDrawerOpen = null;
  });

  return pendingCartDrawerOpen;
}

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      const drawer = getCartDrawer();

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

function installSlideCartBridge() {
  if (!getCartDrawer()) return;

  document.documentElement.dataset.nativeCartDrawer = 'true';

  Object.defineProperty(window, 'SLIDECART_OPEN', {
    configurable: true,
    enumerable: true,
    get() {
      return refreshAndOpenCartDrawer;
    },
    set() {},
  });

  let existingOpenedCallback =
    typeof window.SLIDECART_OPENED === 'function' ? window.SLIDECART_OPENED : null;

  const onSlideCartOpened = () => {
    try {
      existingOpenedCallback?.();
    } finally {
      queueMicrotask(() => {
        window.SLIDECART_CLOSE?.();
        void refreshAndOpenCartDrawer();
      });
    }
  };

  Object.defineProperty(window, 'SLIDECART_OPENED', {
    configurable: true,
    enumerable: true,
    get() {
      return onSlideCartOpened;
    },
    set(callback) {
      if (callback !== onSlideCartOpened) {
        existingOpenedCallback = typeof callback === 'function' ? callback : null;
      }
    },
  });
}

function preventSlideCartSubmit(event) {
  if (!document.getElementById('slidecarthq')) return;

  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.closest('product-form-component')) return;

  const action = new URL(form.action, window.location.href);
  if (!action.pathname.endsWith('/cart/add')) return;

  event.stopPropagation();
}

document.body?.addEventListener('submit', preventSlideCartSubmit);
document.addEventListener('submit', prepareCartDrawerForSubmit, { capture: true });
document.addEventListener('click', prepareCartDrawerForAddClick, { capture: true });
installSlideCartBridge();

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
